import type { PracticeAgentDataRow, PracticeAgentChartDataPoint, PracticeAgentKpiCard } from './types'

const DISPLAY_ACRONYMS = new Set(['AI', 'ATO', 'FYI', 'KPI', 'KPIS', 'LY', 'MYOB', 'WIP', 'XPM'])
const CURRENCY_TOKENS = ['amount', 'ar', 'balance', 'billing', 'billings', 'cash', 'cost', 'debtors', 'fees', 'income', 'invoice', 'margin$', 'net profit', 'profit', 'revenue', 'sales', 'wip']
const PERCENT_TOKENS = ['%', 'margin', 'percent', 'percentage', 'productivity', 'rate', 'recoverability', 'recovery', 'utilisation', 'utilization']

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/
const SLASH_DATE_TIME_PATTERN = /^(\d{1,2}\/\d{1,2}\/\d{2,4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]M)?)?$/i
const TIME_SUFFIX_PATTERN = /\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]M)?$/i

function isDateLike(value: string | number | null): boolean {
  return typeof value === 'string' && (ISO_DATE_PATTERN.test(value) || SLASH_DATE_TIME_PATTERN.test(value))
}

function isMeasureColumn(col: string, rows: PracticeAgentDataRow[]): boolean {
  for (const row of rows) {
    const val = row[col]
    if (val !== null && val !== undefined) {
      return typeof val === 'number'
    }
  }
  return false
}

// Classifies all columns in a single pass, avoiding repeated O(N×M) scans per column.
export function classifyColumns(
  columns: string[],
  rows: PracticeAgentDataRow[],
): { measureCols: string[]; dimensionCols: string[] } {
  const measureCols: string[] = []
  const dimensionCols: string[] = []
  for (const col of columns) {
    if (isMeasureColumn(col, rows)) {
      measureCols.push(col)
    } else {
      dimensionCols.push(col)
    }
  }
  return { measureCols, dimensionCols }
}

function getColValueType(col: string, rows: PracticeAgentDataRow[]): 'string' | 'date' | 'number' | 'unknown' {
  for (const row of rows) {
    const val = row[col]
    if (val === null || val === undefined) continue
    if (typeof val === 'number') return 'number'
    if (typeof val === 'string') return isDateLike(val) ? 'date' : 'string'
  }
  return 'unknown'
}

export function pickLabelColumn(dimensionCols: string[], rows: PracticeAgentDataRow[]): string | null {
  const priority = { string: 0, date: 1, number: 2, unknown: 3 } as const
  let best: string | null = null
  let bestPriority = Infinity
  for (const col of dimensionCols) {
    const p: number = priority[getColValueType(col, rows)]
    if (p < bestPriority) { bestPriority = p; best = col }
  }
  return best
}

export function formatDateTimeLabel(value: string | number | null): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return String(value)
  const slashDateMatch = value.trim().match(SLASH_DATE_TIME_PATTERN)
  if (slashDateMatch?.[1]) return slashDateMatch[1]
  if (ISO_DATE_PATTERN.test(value)) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    }
  }
  return value.replace(TIME_SUFFIX_PATTERN, '')
}

function formatLabel(value: string | number | null): string {
  if (isDateLike(value)) return formatDateTimeLabel(value)
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return String(value)
  return value
}

export function formatFieldDisplayName(value: string): string {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''

  const bracketMatch = trimmed.match(/^(?:'[^']+'|[^[]+)\[([^\]]+)\]$/)
  const source = (bracketMatch?.[1] ?? trimmed).replace(/^'+|'+$/g, '')

  return source
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((token) => {
      const upper = token.toUpperCase()
      if (DISPLAY_ACRONYMS.has(upper) || (/^[A-Z0-9%]+$/.test(token) && token.length <= 5)) {
        return upper
      }

      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
    })
    .join(' ')
}

export function formatFieldReferencesInText(value: string): string {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''

  return trimmed
    .replace(/(?:'[^']+'|[A-Za-z0-9_ ]+)\[([^\]]+)\]\s+\1\b/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/'[^']+'\[([^\]]+)\]/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/\b[A-Za-z0-9_ ]+\[([^\]]+)\]/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/\[([^\]]+)\]/g, (_, field: string) => formatFieldDisplayName(field))
    .replace(/\b([A-Za-z][A-Za-z0-9 %]+)\s+\1\b/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeMetricToken(value: string) {
  return String(value ?? '').toLowerCase().replace(/[_-]+/g, ' ').trim()
}

export function isCurrencyMetric(value: string) {
  const normalized = normalizeMetricToken(value)
  return CURRENCY_TOKENS.some((token) => normalized.includes(token))
}

export function isPercentageMetric(value: string) {
  const normalized = normalizeMetricToken(value)
  return PERCENT_TOKENS.some((token) => normalized.includes(token))
}

type MetricFormatOptions = {
  decimals?: number
}

export function formatMetricValue(value: string | number | null | undefined, metricName?: string | null, options?: MetricFormatOptions): string {
  if (value === null || value === undefined || value === '') return '—'

  const decimals = options?.decimals ?? 0

  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, ''))
    if (!Number.isFinite(parsed)) {
      return value
    }
    return formatMetricValue(parsed, metricName, options)
  }

  if (metricName && isPercentageMetric(metricName)) {
    return `${value.toLocaleString('en-AU', {
      minimumFractionDigits: value % 1 === 0 ? 0 : 1,
      maximumFractionDigits: value % 1 === 0 ? 0 : 1,
    })}%`
  }

  if (metricName && isCurrencyMetric(metricName)) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      notation: 'standard',
      maximumFractionDigits: decimals,
      minimumFractionDigits: 0,
    }).format(value)
  }

  return value.toLocaleString('en-AU', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
  })
}

export function deriveChartData(columns: string[], rows: PracticeAgentDataRow[]): PracticeAgentChartDataPoint[] {
  if (!rows.length || !columns.length) return []

  const { measureCols, dimensionCols } = classifyColumns(columns, rows)

  if (!measureCols.length) return []

  const labelCol = pickLabelColumn(dimensionCols, rows)
  if (!labelCol) return []

  const primaryMeasure = measureCols[0]
  if (!primaryMeasure) return []

  return rows.map((row) => {
    const labelValue = row[labelCol]
    const label = formatLabel(labelValue ?? null)
    const value = typeof row[primaryMeasure] === 'number' ? (row[primaryMeasure] as number) : 0
    const series = measureCols.map((m) => ({
      name: m,
      value: typeof row[m] === 'number' ? (row[m] as number) : 0,
    }))
    return { label, value, series }
  })
}

export function deriveChartDataForDimension(
  columns: string[],
  rows: PracticeAgentDataRow[],
  labelCol: string,
  primaryMeasureOverride?: string | null,
): PracticeAgentChartDataPoint[] {
  if (!rows.length || !columns.length) return []
  const { measureCols } = classifyColumns(columns, rows)
  if (!measureCols.length) return []
  const resolvedPrimary =
    primaryMeasureOverride && measureCols.includes(primaryMeasureOverride)
      ? primaryMeasureOverride
      : measureCols[0]
  if (!resolvedPrimary) return []

  const accumMap = new Map<string, { value: number; series: Map<string, number> }>()
  for (const row of rows) {
    const label = formatLabel(row[labelCol] ?? null)
    let acc = accumMap.get(label)
    if (!acc) {
      acc = { value: 0, series: new Map(measureCols.map((m) => [m, 0])) }
      accumMap.set(label, acc)
    }
    acc.value += typeof row[resolvedPrimary] === 'number' ? (row[resolvedPrimary] as number) : 0
    for (const m of measureCols) {
      acc.series.set(m, (acc.series.get(m) ?? 0) + (typeof row[m] === 'number' ? (row[m] as number) : 0))
    }
  }

  return Array.from(accumMap.entries()).map(([label, acc]) => ({
    label,
    value: acc.value,
    series: Array.from(acc.series.entries()).map(([name, value]) => ({ name, value })),
  }))
}

export function getBarAxisMin(values: number[]): number {
  const finiteValues = values.filter(Number.isFinite)
  if (!finiteValues.length) return 0

  const minValue = Math.min(...finiteValues)
  return minValue < 0 ? minValue : 0
}

export function getBarAxisMaxFallback(values: number[]): number | 'dataMax' {
  const finiteValues = values.filter(Number.isFinite)
  if (!finiteValues.length) return 'dataMax'

  return Math.max(...finiteValues) < 0 ? 0 : 'dataMax'
}

export function deriveKpiCards(columns: string[], rows: PracticeAgentDataRow[]): PracticeAgentKpiCard[] {
  if (!rows.length || !columns.length) return []

  const { measureCols, dimensionCols } = classifyColumns(columns, rows)

  if (!measureCols.length) return []

  const labelCol = pickLabelColumn(dimensionCols, rows)
  const primaryMeasure = measureCols[0]
  if (!primaryMeasure) return []

  const measureLabel = formatFieldDisplayName(primaryMeasure)

  // Per-row cards: one card per data row (label = dimension value, value = primary measure)
  return rows.map((row) => {
    const label = labelCol ? formatLabel(row[labelCol] ?? null) : ''
    const value = typeof row[primaryMeasure] === 'number' ? (row[primaryMeasure] as number) : row[primaryMeasure] ?? ''
    return { label, value, measureLabel }
  })
}
